import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import type { HistoricoDoProduto } from '../../produtos/historicoDoProduto'
import { carregarVendasDoProduto } from '../../produtos/vendasDoProduto'
import { anosDoHistorico, semestreDe, visaoDoHistorico, rotuloDoMes } from '../../produtos/periodosDoHistorico'
import { clientesDoPeriodo } from '../../produtos/historicoDoCliente'
import type { Semestre } from '../../produtos/periodosDoHistorico'
import { formatarMoeda } from '../../lib/formatos'

// A aba Historico no modo de VER (print dela, 23/09 -- spec §4c). Comparacao em
// NUMERO, nunca em %: com o volume de hoje (149 itens no banco inteiro), 1
// contra 2 vendas viraria "+100%" e pareceria um salto que nao existe.
// Rodada 2 (24/09 tarde): filtros ANO + SEMESTRE; o filtro Cliente saiu -- o cliente
// vai ganhar tela propria, a alinhar com ela.
export default function AbaHistoricoVendo(props: { produtoId: string; historico: HistoricoDoProduto | null | undefined }) {
  const navegar = useNavigate()
  const [ano, setAno] = useState(() => new Date().getFullYear())
  const [semestre, setSemestre] = useState<Semestre>(() => semestreDe(new Date()))
  // A barra tocada no grafico ("2026-09"): numeros e lista mostram aquele mes.
  // Tocar de novo, ou em "Voltar ao semestre", volta ao semestre inteiro.
  const [mesTocado, setMesTocado] = useState<string | null>(null)
  const [verTodas, setVerTodas] = useState(false)

  const vendas = useLiveQuery(() => carregarVendasDoProduto(props.produtoId), [props.produtoId])
  if (vendas === undefined) return <p className="text-sm text-slate-500">Carregando…</p>

  const visao = visaoDoHistorico(vendas, ano, semestre, mesTocado)
  const { resumo, resumoAnterior } = visao
  const maior = Math.max(1, ...visao.barras.map((b) => b.total))
  // Uma linha por cliente (escolha dela, 24/09 noite); tocar abre a tela do cliente.
  const clientes = clientesDoPeriodo(visao.lista)
  const lista = verTodas ? clientes : clientes.slice(0, 5)
  const estiloFiltro = 'mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 bg-white text-sm text-slate-800'
  const anterior = (texto: string) => (resumoAnterior === null ? null : `período anterior: ${texto}`)

  const cards: Array<[string, string, string | null]> = [
    ['Total vendido', formatarMoeda(resumo.total), anterior(formatarMoeda(resumoAnterior?.total ?? 0))],
    ['Quantidade', `${resumo.unidades} un.`, anterior(`${resumoAnterior?.unidades ?? 0} un.`)],
    ['Clientes que compraram', String(resumo.clientes), anterior(String(resumoAnterior?.clientes ?? 0))],
    ['Ticket médio', resumo.ticketMedio == null ? '—' : formatarMoeda(resumo.ticketMedio),
      anterior(resumoAnterior?.ticketMedio == null ? '—' : formatarMoeda(resumoAnterior.ticketMedio))],
  ]

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-slate-500">Ano
          <select className={estiloFiltro} value={ano}
            onChange={(e) => { setAno(Number(e.target.value)); setMesTocado(null); setVerTodas(false) }}>
            {anosDoHistorico(vendas, new Date()).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="block text-xs text-slate-500">Período
          <select className={estiloFiltro} value={semestre}
            onChange={(e) => { setSemestre(Number(e.target.value) as Semestre); setMesTocado(null); setVerTodas(false) }}>
            <option value={1}>1º semestre</option>
            <option value={2}>2º semestre</option>
          </select>
        </label>
      </div>
      {visao.mesTocado !== null && (
        <p className="text-xs text-slate-600">Números de <strong>{rotuloDoMes(visao.mesTocado)}</strong></p>
      )}

      <div className="grid grid-cols-2 gap-2">
        {cards.map(([rotulo, valor, comparacao]) => (
          <div key={rotulo} className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
            <p className="text-xs text-slate-500">{rotulo}</p>
            <p className="text-lg font-bold text-slate-900">{valor}</p>
            {comparacao !== null && <p className="text-xs text-slate-500">{comparacao}</p>}
          </div>
        ))}
      </div>
      {props.historico && (
        <p className="text-xs text-slate-600">
          Média mensal: {props.historico.mediaVendaMensal} un.
          {props.historico.mediaVendaPorCliente != null && ` · ${props.historico.mediaVendaPorCliente}% dos clientes da marca compraram`}
        </p>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <p className="text-sm font-semibold text-slate-800">Evolução das vendas ({semestre}º semestre de {ano})</p>
        <p className="text-xs text-slate-500 mb-2">Toque numa barra para ver as vendas do mês.</p>
        <div className="flex items-end gap-2 h-32">
          {visao.barras.map((b) => (
            <button
              key={b.chave} type="button" aria-label={`Vendas de ${rotuloDoMes(b.chave)}`} aria-pressed={visao.mesTocado === b.chave}
              onClick={() => { setMesTocado(visao.mesTocado === b.chave ? null : b.chave); setVerTodas(false) }}
              className="flex-1 flex flex-col items-center justify-end h-full"
            >
              <div
                className={`w-full rounded-t ${visao.mesTocado === b.chave ? 'bg-teal-700' : 'bg-emerald-400'}`}
                style={{ height: `${(b.total / maior) * 100}%` }} title={formatarMoeda(b.total)}
              />
              <span className={`mt-1 text-[10px] ${visao.mesTocado === b.chave ? 'font-bold text-teal-800' : 'text-slate-500'}`}>{b.rotulo}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <div className="flex justify-between items-center mb-1">
          <p className="text-sm font-semibold text-slate-800">
            {visao.mesTocado === null ? 'Clientes do semestre' : `Clientes de ${rotuloDoMes(visao.mesTocado)}`}
          </p>
          {visao.mesTocado !== null && (
            <button type="button" onClick={() => { setMesTocado(null); setVerTodas(false) }} className="text-xs text-slate-500 underline">
              Voltar ao semestre
            </button>
          )}
          {clientes.length > 5 && (
            <button type="button" onClick={() => setVerTodas((v) => !v)} className="text-xs border border-slate-300 rounded-full px-3 py-1">
              {verTodas ? 'Ver menos' : 'Ver todos ›'}
            </button>
          )}
        </div>
        {lista.length === 0 ? (
          <p className="text-sm text-slate-500">{visao.mesTocado === null ? 'Nenhuma venda neste semestre.' : 'Nenhuma venda neste mês.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lista.map((c) => (
              <li key={c.clienteId}>
                <button type="button" onClick={() => navegar(`/produtos/${props.produtoId}/clientes/${c.clienteId}`)} className="w-full text-left py-2 flex gap-2 items-center">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-800 truncate">{c.nome}</span>
                    <span className="block text-xs text-slate-500">{c.compras} {c.compras === 1 ? 'compra' : 'compras'}</span>
                  </span>
                  <span className="text-right shrink-0 text-sm text-slate-800">{c.unidades} un. · {formatarMoeda(c.total)}</span>
                  <span aria-hidden="true" className="text-slate-400">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
